import type { ServiceLanguage } from "@albertine/i18n";
import * as z from "zod";
import { GooglePlace } from "../google";
import { OfferingCategory } from "../offeringCategory";
import { ISODateTime as IsoDateTimeString, LongString, UrlString } from "../primitives";
import { Tag } from "../tags";
import { VisibilityFilter } from "./apiDtos";

export const CatalogLocation = z
    .object({
        administrativeAreaLevel1: z.string().optional(),
        administrativeAreaLevel2: z.string().optional(),
        locality: z.string().optional(),
        sublocalityLevel1: z.string().optional(),
        neighborhood: z.string().optional(),
        country: z.string().optional(),
        lat: z.number().optional(),
        lon: z.number().optional(),
        point: z
            .object({
                lat: z.number(),
                lon: z.number(),
            })
            .optional(),
        countryShort: z.string().optional(),
        postalTown: z.string().optional(),
        sublocalityLevel2: z.string().optional(),
        streetAddress: z.string().optional(),
    })
    .optional();

export type CatalogLocation = z.infer<typeof CatalogLocation>;

export const CatalogItemTranslation = z.object({
    name: z.string().optional().nullable(),
    address: LongString.optional().nullable(),
    description: z.string().optional().nullable(),
    headline: z.string().optional().nullable(),
    benefits: z.string().optional().nullable(),
    bookingPolicy: z.string().optional().nullable(),
    eventDates: z.string().optional().nullable(),
    localInsights: z.string().optional().nullable(),
    travelItineraryNotes: z.string().optional().nullable(),
    keyDetail: z.string().optional().nullable(),
});

export type CatalogItemTranslation = z.infer<typeof CatalogItemTranslation>;

export const CatalogArticle = z.object({
    id: z.string(),
    googlePlaceId: z.string().optional().nullable(),
    googlePlaceData: GooglePlace.optional().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    address: z.string().nullable(),
    website: UrlString.nullable(),
    offeringCategories: z.array(OfferingCategory).optional().nullable(),
    suppliers: z.array(z.string()).optional().nullable(),
    tags: z.array(Tag).optional().nullable(),
    notes: z.string().optional().nullable(),
    images: z.array(z.string()).optional().nullable(),
    localInsights: z.string().optional().nullable(),
    source: z.string().optional().nullable(),
    location: CatalogLocation,
    published: z.boolean(),
    shared: z.boolean(),
    createdAt: z.string(),
    albertineVerified: IsoDateTimeString.optional().nullable(),
    recentlyOpened: IsoDateTimeString.optional().nullable(),
    slug: z.string().nullable(),
    cityGuideLocation: z.string().nullable(),
    headline: z.string().optional().nullable(),
    benefits: z.string().optional().nullable(),
    bookingPolicy: z.string().optional().nullable(),
    eventDates: z.string().optional().nullable(),
    travelItineraryNotes: z.string().optional().nullable(),
    keyDetail: z.string().optional().nullable(),
    translations: z
        .object({
            de: CatalogItemTranslation.optional(),
            fr: CatalogItemTranslation.optional(),
        })
        .optional(),
});

export type CatalogArticle = z.infer<typeof CatalogArticle>;

export const CatalogArticleSearchResult = CatalogArticle.extend({
    distance: z.number().optional(),
});
export type CatalogArticleSearchResult = z.infer<typeof CatalogArticleSearchResult>;

export const CatalogItemsSearchResponseDto = z.object({
    total: z.number(),
    catalogItems: z.array(CatalogArticleSearchResult),
});

export type CatalogItemsSearchResponseDto = z.infer<
    typeof CatalogItemsSearchResponseDto
>;

export const GetCatalogItemByIdParams = z.object({
    id: z.string(),
});

export const GetCatalogItemsQuery = z
    .object({
        ids: z.string().optional(),
        slugs: z.string().optional(),
    })
    .refine((data) => data.ids || data.slugs, {
        error: "ids or slugs must be provided",
    });

export const GetAllThemesQuery = z.object({
    from: z.coerce.number().optional(),
    size: z.coerce.number().optional(),
});

export const GetCatalogItemsByThemeIdParams = z.object({
    themeId: z.string(),
});

export const GetThemeCatalogItemsQuery = z.object({
    from: z.coerce.number().optional(),
    size: z.coerce.number().optional(),
});

// Catalog search filter types (for CatalogSearchRequest)
export const CatalogLocationFilter = z.object({
    administrativeAreaLevel1: z.string().optional(),
    administrativeAreaLevel2: z.string().optional(),
    locality: z.string().optional(),
    sublocalityLevel1: z.string().optional(),
    neighborhood: z.string().optional(),
    country: z.string().optional(),
    lat: z.number().optional(),
    lon: z.number().optional(),
});

export type CatalogLocationFilter = z.infer<typeof CatalogLocationFilter>;

export const BoundsFilter = z.object({
    top: z.number(),
    bottom: z.number(),
    left: z.number(),
    right: z.number(),
});

export type BoundsFilter = z.infer<typeof BoundsFilter>;

export const CatalogPrimaryFilters = z.object({
    searchTerm: z.string().optional(),
    offeringCategories: z.array(OfferingCategory).optional(),
    locationSearchTerm: z.string().optional(),
    location: CatalogLocationFilter.optional(),
    bounds: BoundsFilter.optional(),
    recommendedItemIds: z.array(z.string()).optional(),
});

export type CatalogPrimaryFilters = z.infer<typeof CatalogPrimaryFilters>;

export const CatalogQuerySecondaryFilters = z.object({
    source: z.array(z.string()).optional(),
    suppliers: z.array(z.string()).optional(),
    recentlyOpened: z.boolean().optional(),
    albertineVerified: z.boolean().optional(),
    missingTranslations: z.boolean().optional(),
    tags: z.array(Tag).optional(),
    published: z.array(VisibilityFilter).optional(),
});

export type CatalogQuerySecondaryFilters = z.infer<typeof CatalogQuerySecondaryFilters>;

export const CatalogQueryFilters = z.object({
    view: z.enum(["list", "map"]).optional(),
    primaryFilters: CatalogPrimaryFilters,
    secondaryFilters: CatalogQuerySecondaryFilters.optional(),
});

export type CatalogQueryFilters = z.infer<typeof CatalogQueryFilters>;

export function findTranslations(
    item: CatalogArticle,
    serviceLanguage: ServiceLanguage,
): CatalogItemTranslation & { name: string; keyDetail: string } {
    const englishBaseline = {
        address: item.address ?? null,
        benefits: item.benefits ?? null,
        bookingPolicy: item.bookingPolicy ?? null,
        description: item.description ?? null,
        eventDates: item.eventDates ?? null,
        headline: item.headline ?? null,
        localInsights: item.localInsights ?? null,
        name: item.name,
        travelItineraryNotes: item.travelItineraryNotes ?? null,
        keyDetail: item.keyDetail ?? "",
    };

    if (serviceLanguage === "en") {
        return englishBaseline;
    }

    const translation = item?.translations?.[serviceLanguage] || undefined;

    return translation
        ? {
              address: translation.address ?? englishBaseline.address,
              benefits: translation.benefits ?? englishBaseline.benefits,
              bookingPolicy: translation.bookingPolicy ?? englishBaseline.bookingPolicy,
              description: translation.description ?? englishBaseline.description,
              eventDates: translation.eventDates ?? englishBaseline.eventDates,
              headline: translation.headline ?? englishBaseline.headline,
              localInsights: translation.localInsights ?? englishBaseline.localInsights,
              name: translation.name ?? englishBaseline.name,
              travelItineraryNotes:
                  translation.travelItineraryNotes ??
                  englishBaseline.travelItineraryNotes,
              keyDetail: translation.keyDetail ?? englishBaseline.keyDetail,
          }
        : englishBaseline;
}
